import React, { useState, useRef } from "react";
import { Camera, X, Upload, Loader, FileText } from "lucide-react";

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

async function uploadToCloudinary(file) {
  const form = new FormData();
  form.append("file", file);
  form.append("upload_preset", UPLOAD_PRESET);
  form.append("folder", "orello-reports");
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  const type = file.type === "application/pdf" ? "pdf"
             : file.type.startsWith("video") ? "video" : "image";
  return { url: data.secure_url, type, public_id: data.public_id, name: file.name };
}

const isPdf = (f) => f?.type === "pdf" || (f?.url || "").toLowerCase().includes(".pdf");

export default function PhotoUpload({ value = [], onChange, maxFiles = 10, label = "Add photos or videos",
  accept = "image/*,video/*", hint = "Photos and videos." }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const handleFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(Array.from(files).slice(0, maxFiles - value.length).map(uploadToCloudinary));
      onChange([...value, ...uploaded]);
    } catch (e) {
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {value.map((f, i) => (
          <div key={i} className="relative group">
            {isPdf(f) ? (
              <a href={f.url} target="_blank" rel="noreferrer"
                className="h-20 w-20 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center gap-1">
                <FileText className="h-6 w-6 text-red-400" />
                <span className="text-[9px] text-gray-500 px-1 truncate w-full text-center">{f.name || "PDF"}</span>
              </a>
            ) : f.type === "video" ? (
              <video src={f.url} className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
            ) : (
              <img src={f.url} alt="" className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
            )}
            <button onClick={() => remove(i)}
              className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {value.length < maxFiles && (
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            className="h-20 w-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-indigo-400 hover:bg-[#FFF4ED] transition-colors disabled:opacity-50">
            {uploading ? <Loader className="h-5 w-5 text-gray-400 animate-spin" /> : <Camera className="h-5 w-5 text-gray-400" />}
            <span className="text-[10px] text-gray-400">{uploading ? "Uploading…" : label}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept={accept} multiple hidden
        onChange={(e) => handleFiles(e.target.files)} />
      <p className="text-xs text-gray-400">{hint} Max {maxFiles} files.</p>
    </div>
  );
}